/**
 * Application surface manifest — the primary sidebar sections and the Setup
 * sub-sections, as DATA (no icons, no React), so every consumer can read the
 * same list:
 *
 *   - `apps/web` renders it (attaching its own icons — see
 *     `apps/web/src/lib/navigation.ts`).
 *   - `apps/web` also derives the post-sign-in landing page from it.
 *   - `apps/smoke` generates one post-release smoke check per entry, so a new
 *     section added here is smoke-covered the moment it ships.
 *
 * Adding a section: add it here once. Everything above follows.
 *
 * Icons deliberately live in the web app rather than here — `@clockwise/shared`
 * is imported by the API and by Node-only test suites, and must not pull in
 * `lucide-react`.
 */
import type { SystemRolePermissionId } from './permissions';

export interface NavItem {
  title: string;
  url: string;
  permission: SystemRolePermissionId;
  /**
   * Further ids the surface ALSO requires, beyond {@link permission}.
   *
   * One nav item used to mean one permission, and every consumer — the sidebar
   * filter, the landing resolver, the smoke suite — was written on that
   * assumption. `/dashboards` broke it: it needs `dashboard.view` to reach the
   * section AND `report.view` for the tiles to return anything. Checking only
   * the first id lands a user on a page that then refuses them, so consumers
   * must ask {@link hasNavAccess} rather than reading `permission` directly.
   *
   * `permission` stays the PRIMARY id — the one a surface is named for, and
   * the one to quote at a user who cannot get in.
   */
  alsoRequires?: readonly SystemRolePermissionId[];
}

/** Every id a nav item needs, primary first. */
export function navItemPermissions(item: NavItem): readonly SystemRolePermissionId[] {
  return [item.permission, ...(item.alsoRequires ?? [])];
}

/** Whether these permissions open the nav item — ALL of its ids, not just the primary. */
export function hasNavAccess(item: NavItem, permissions: Set<string>): boolean {
  return navItemPermissions(item).every((id) => permissions.has(id));
}

/**
 * Primary sidebar navigation, in display order. Where a user lands after
 * sign-in is a separate precedence ({@link LANDING_ORDER}), so reordering the
 * sidebar never moves anyone's landing page.
 */
export const navItems: NavItem[] = [
  // `dashboard.view`, which a role holds ON TOP of `report.view` — the tiles'
  // numbers still come from the analytics endpoint, which asks for
  // `report.view`. This entry was on `report.view` itself until it turned out that
  // gated Dashboards on the SAME id as Reports two rows down, so every user
  // who could open a report had this section too and no role setting could
  // separate them. See `permissions.ts`.
  // `alsoRequires` because the tiles are fetched under `report.view`'s row
  // scope: `dashboard.view` alone opens a frame whose every tile refuses.
  {
    title: 'Dashboards',
    url: '/dashboards',
    permission: 'dashboard.view',
    alsoRequires: ['report.view'],
  },
  { title: 'Timesheets', url: '/timesheets', permission: 'timesheet.view' },
  { title: 'Approvals', url: '/approvals', permission: 'timesheet.approve' },
  { title: 'Invoices', url: '/invoices', permission: 'invoice.view' },
  { title: 'Customers', url: '/customers', permission: 'customer.view' },
  { title: 'Employees', url: '/users', permission: 'user.view' },
  { title: 'Reports', url: '/reports', permission: 'report.view' },
  { title: 'Audit log', url: '/audit', permission: 'audit.view' },
];

/**
 * Landing-page precedence over the primary nav urls: the first one a user can
 * access is where they land after sign-in ({@link resolveLandingPath}). Kept
 * apart from the sidebar order so time entry stays the default landing for
 * anyone who can log time, whatever sits at the top of the sidebar. Must list
 * every `navItems` url exactly once (`navigation.test.ts` enforces it).
 */
export const LANDING_ORDER: readonly string[] = [
  '/timesheets',
  '/approvals',
  '/reports',
  '/dashboards',
  '/customers',
  '/invoices',
  '/users',
  '/audit',
];

export interface SetupNavItem {
  title: string;
  to: string;
  permission: SystemRolePermissionId;
}

/**
 * Setup sub-sections, in display order. Single source of truth: the Setup
 * sidebar (`_authenticated/setup.tsx`), the "Setup" nav gate, the landing
 * resolver and the smoke suite all read this list, so adding a section can
 * never drift from the permission that gates it.
 */
export const setupNav: SetupNavItem[] = [
  { title: 'Company', to: '/setup/company', permission: 'company.view' },
  { title: 'Business Units', to: '/setup/business-units', permission: 'business_unit.view' },
  { title: 'Activities', to: '/setup/activities', permission: 'activity.view' },
  { title: 'Rate cards', to: '/setup/ratecards', permission: 'ratecard.view' },
  {
    title: 'Expense catalog',
    to: '/setup/expense-catalogs',
    permission: 'expensecatalog.view',
  },
  { title: 'System roles', to: '/setup/system-roles', permission: 'system_role.view' },
  { title: 'Service roles', to: '/setup/service-roles', permission: 'service_role.view' },
  { title: 'Departments', to: '/setup/departments', permission: 'department.view' },
  { title: 'Skills', to: '/setup/skills', permission: 'skills.view' },
  {
    title: 'Chart of accounts',
    to: '/setup/chart-of-accounts',
    permission: 'chart_of_accounts.view',
  },
  { title: 'Holidays', to: '/setup/holidays', permission: 'holiday.view' },
  { title: 'Custom fields', to: '/setup/custom-fields', permission: 'custom_fields.view' },
  {
    title: 'Approval policies',
    to: '/setup/approval-policies',
    permission: 'approval_policy.view',
  },
  { title: 'Notifications', to: '/setup/notifications', permission: 'notifications.view' },
];

/**
 * Permissions that make the "Setup" nav item visible — derived from
 * {@link setupNav} so it can never fall out of sync with the sections it gates.
 */
export const SETUP_PERMISSIONS: readonly SystemRolePermissionId[] = setupNav.map(
  (item) => item.permission,
);

/**
 * The first setup sub-section the user can open, or `null` if none. Used both
 * for the landing resolver and for the `/setup` index redirect so we never send
 * a user to a setup page they lack the permission for.
 */
export function resolveFirstSetupPath(permissions: Set<string>): string | null {
  return setupNav.find((item) => permissions.has(item.permission))?.to ?? null;
}

/**
 * The path a user should land on given their permissions: the first primary
 * nav item they can access, else the first Setup section they can open, else
 * `null` when they can access nothing at all (the caller should route those
 * users to `/no-access` rather than into a permission-gated page — which would
 * bounce them straight back out and loop).
 */
export function resolveLandingPath(permissions: Set<string>): string | null {
  const firstNav = LANDING_ORDER.find((url) => {
    const item = navItems.find((i) => i.url === url);
    // `hasNavAccess`, not `permissions.has(item.permission)` — a nav item may
    // need more than its primary id, and landing someone on a page that then
    // refuses them is the loop this function's contract promises to avoid.
    return item !== undefined && hasNavAccess(item, permissions);
  });
  if (firstNav) return firstNav;
  return resolveFirstSetupPath(permissions);
}

/** Human label for a landing/setup path, for "Go to X" affordances. */
export function landingTitleForPath(path: string): string {
  return (
    navItems.find((item) => item.url === path)?.title ??
    setupNav.find((item) => item.to === path)?.title ??
    'Setup'
  );
}
